<template>
  <div class="project-container">
    <header class="header">
      <div class="flex-container">
        <div class="image-container header-img-container">
          <img :src="require('@/assets/image/CGJ2024-Affiche-8eme-edition.jpg')" alt="Affiche Code Game Jam" class="header-image" loading="lazy"/>
        </div>
        <div class="text-container">
          <h1>{{ $t('cgj.title') }}</h1>
          <p class="subtitle">{{ $t('cgj.subtitle') }}</p>
        </div>
      </div>
    </header>

    <section class="about-project">
      <h2>{{ $t('cgj.aboutTitle') }}</h2>
      <div class="content-grid">
        <div class="text-block">
          <p v-html="$t('cgj.intro1')"></p>
          <p v-html="$t('cgj.intro2')"></p>
        </div>
        <div class="highlight-box">
           <div class="stat-item">
             <span class="mdi mdi-timer-outline stat-icon"></span>
             <span class="stat-text">{{ $t('cgj.statTime') }}</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-account-group-outline stat-icon"></span>
             <span class="stat-text">{{ $t('cgj.statTeam') }}</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-controller stat-icon"></span>
             <span class="stat-text">{{ $t('cgj.statType') }}</span>
           </div>
        </div>
      </div>
    </section>

    <section class="role-details">
      <h2>{{ $t('cgj.roleTitle') }}</h2>
      <div class="tech-grid">
        <div class="tech-card">
          <span class="mdi mdi-cube-outline tech-icon"></span>
          <div class="tech-text">
            <h4>{{ $t('cgj.roleArch') }}</h4>
            <ul>
              <li v-html="$t('cgj.role1')"></li>
              <li v-html="$t('cgj.role2')"></li>
            </ul>
          </div>
        </div>
        <div class="tech-card">
          <span class="mdi mdi-brain tech-icon"></span>
          <div class="tech-text">
            <h4>{{ $t('cgj.roleAi') }}</h4>
            <ul>
              <li v-html="$t('cgj.role3')"></li>
              <li v-html="$t('cgj.role4')"></li>
            </ul>
          </div>
        </div>
      </div>
      <p class="conclusion-text" v-html="$t('cgj.conclusion')"></p>
    </section>

    <section class="architecture-details">
      <h2>{{ $t('cgj.aiTitle') }}</h2>
      <p class="intro-text" v-html="$t('cgj.aiIntro')"></p>
      <div class="tech-grid ai-grid">
        <div class="tech-card ai-card">
          <span class="mdi mdi-map-marker-path tech-icon"></span>
          <div class="tech-text">
            <p v-html="$t('cgj.aiNavmesh')"></p>
          </div>
        </div>
        <div class="tech-card ai-card">
          <span class="mdi mdi-radar tech-icon"></span>
          <div class="tech-text">
            <p v-html="$t('cgj.aiCollider')"></p>
          </div>
        </div>
      </div>
    </section>

    <section class="visuals-section">
      <h2>{{ $t('cgj.imageTitle') }}</h2>
      <div class="image-wrapper">
        <img :src="require('@/assets/image/cgj-preview-jeu.png')" alt="Capture d'écran du jeu" class="doc-image" loading="lazy"/>
      </div>
    </section>

    <section class="technical-details">
      <h2>{{ $t('cgj.learningsTitle') }}</h2>
      <div class="competencies-grid">
        <InfraCompetencyCard 
          icon="mdi-lightning-bolt-outline"
          title="Gestion du Stress"
          :description="$t('cgj.learning1')"
        />
        <InfraCompetencyCard 
          icon="mdi-github"
          title="Git Collaboratif"
          :description="$t('cgj.learning2')"
        />
        <InfraCompetencyCard 
          icon="mdi-controller"
          title="Game Development"
          :description="$t('cgj.learning3')"
        />
        <InfraCompetencyCard 
          icon="mdi-account-group-outline"
          title="Coordination d'Équipe"
          :description="$t('cgj.learning4')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import InfraCompetencyCard from '@/component/project/infra/InfraCompetencyCard.vue';

@Component({
  components: {
    InfraCompetencyCard
  }
})
export default class CGJComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'TTInterfaces', sans-serif;
  line-height: 1.7;
  color: var(--ln-text-primary);
  background-color: transparent;
  width: 100%;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-elevated);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-lg);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 12px 48px var(--ln-glass-shadow);
}

.flex-container {
  display: flex;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.header-img-container {
  width: 180px;
  flex-shrink: 0;
}

.text-container {
  flex: 1 1 400px;
  text-align: left;
}

.header h1 {
  font-size: clamp(2rem, 6vw, 3rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 1.25rem;
  color: var(--ln-text-secondary);
  font-weight: 500;
  opacity: 0.9;
}

.header-image {
  width: 100%;
  max-width: 180px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  transition: transform 0.5s ease;
}

.header-image:hover {
  transform: scale(1.05) translateY(-5px);
}

section {
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 8px 32px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

section:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px var(--ln-glass-shadow-elevated);
}

h2 {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
}

h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 80px;
  height: 4px;
  background: linear-gradient(to right, var(--ln-terracotta-subtle), var(--ln-stone-grey));
  border-radius: 2px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3rem;
  align-items: center;
}

.highlight-box {
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border-strong);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item:last-child { margin-bottom: 0; }
.stat-icon { font-size: 2.2rem; color: var(--ln-teal-deep); }
.stat-text { font-weight: 600; color: var(--ln-text-primary); }

.tech-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.tech-card {
  display: flex;
  flex-direction: column;
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
}

.tech-icon { font-size: 3rem; color: var(--ln-teal-deep); margin-bottom: 1rem; }
.tech-text h4 { margin: 0 0 1rem 0; color: var(--ln-text-primary); font-size: 1.3rem; }
.tech-text ul { list-style: none; padding: 0; margin: 0; }
.tech-text li { font-size: 0.95rem; color: var(--ln-text-secondary); margin-bottom: 0.8rem; position: relative; padding-left: 1.2rem; }
.tech-text li::before { content: '→'; position: absolute; left: 0; color: var(--ln-terracotta-subtle); }

.conclusion-text {
  margin-top: 1rem;
  font-style: italic;
  font-size: 1rem;
  color: var(--ln-text-secondary);
  border-left: 3px solid var(--ln-terracotta-subtle);
  padding-left: 1.5rem;
}

.intro-text {
  margin-bottom: 2rem;
  color: var(--ln-text-secondary);
  font-size: 1.05rem;
}

.ai-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.ai-card {
  align-items: center;
  text-align: center;
  padding: 2.5rem;
}

.ai-card .tech-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, var(--ln-teal-deep) 0%, var(--ln-terracotta-subtle) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.ai-card p {
  font-size: 1.05rem;
  color: var(--ln-text-secondary);
  line-height: 1.6;
  margin: 0;
}

/* Visuals section */
.image-wrapper {
  background: var(--ln-glass-surface);
  padding: 1.5rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
}

.doc-image {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  transition: transform 0.3s ease;
}

.doc-image:hover { transform: scale(1.01); }

/* Competencies */
.competencies-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.academic-context {
  padding: 2rem;
  background: var(--ln-glass-surface);
  border-radius: var(--ln-radius-md);
  border-top: 2px solid var(--ln-stone-grey);
}

.dacs-list {
  list-style: none;
  padding: 0;
  margin-top: 1rem;
}

.dacs-list li {
  font-size: 0.95rem;
  color: var(--ln-text-secondary);
  margin-bottom: 0.8rem;
  padding-left: 1.5rem;
  position: relative;
}

.dacs-list li::before {
  content: '★';
  position: absolute;
  left: 0;
  color: var(--ln-stone-grey);
}

@media (max-width: 992px) {
  .content-grid, .tech-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .header { padding: 1.5rem; }
  .flex-container { gap: 1rem; flex-direction: column; }
  .text-container { text-align: center; flex: 1 1 auto; }
  section { padding: 1.5rem; }
}
</style>
